import { call, delay } from 'redux-saga/effects';
import windowLocation from './windowLocation';

const promisifyModel = (model) => {
  return new Promise((resolve, reject) => {
    model.save(null, {
      success: (res) => {
        resolve(res);
      },
      error: (err) => {
        reject(err);
      }
    });
  });
};

const parseResponse = (response) => {
  // Header might have a char encoding
  // similar to text/plain; charset=utf-8
  let contentType;
  if (response.headers.has('content-type')) {
    contentType = response.headers.get('content-type').split(';')[0];
  }
  switch (contentType) {
    case 'application/json':
      return response.json();
    default:
      return response.text();
  }
};

const checkStatus = (response) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  if (response.status === 403 && windowLocation.getPathname() !== '/login') {
    windowLocation.assign('/login');
    return response;
  }
  if (response.status === 401 && windowLocation.getPathname().indexOf('/login') === -1) {
    windowLocation.assign(`/login?ref=${window.encodeURIComponent(windowLocation.getPathname())}`);
    return response;
  }
  const error = new Error(response.statusText);
  error.response = response;
  return parseResponse(response)
    .catch(() => {
      throw error;
    })
    .then((body) => {
      error.body = body;
      throw error;
    });
};

const apiClient = (url, options={}) => {
  const headers = Object.assign({},
    {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    options.headers);
  return fetch(url, {...options, headers, credentials: 'same-origin'})
    .then(checkStatus)
    .then(parseResponse);
};

// For use within our Redux sagas (depends on redux-saga library to implement
// *and* to consume). E.g., Caller would have following code:
// `const response = yield call(apiClientWithRetries, { maxAttempts: 5, url, fetchOptions });`
// where `call` is imported from `redux-saga/effects`.
// Based on https://redux-saga.js.org/docs/recipes/#retrying-xhr-calls
function* apiClientWithRetries({maxAttempts = 3, url, fetchOptions}) {
  let error;

  for(let i = 0; i < maxAttempts; i++) {
    try {
      const apiResponse = yield call(apiClient, url, fetchOptions);
      return apiResponse;
    } catch(e) {
      error = e;
      if(i < maxAttempts - 1) {
        yield delay(1000);// wait 1sec before retrying
      }
    }
  }

  throw error;
}

export default apiClient;
export {
  promisifyModel,
  apiClientWithRetries
};
